/**
 * Governed settings: every workbench and node setting the organisation may set, drawn from the
 * workbench's own declarations, grouped as its Settings view groups them. Each row shows the
 * organisation's value or "not set", whether it is locked (the organisation's value wins and
 * people cannot change it) or a default (people may change it), and takes a value checked
 * against the declaration. The same editor sets a project's overrides, which win over the tenant's.
 */

import { SETTINGS_DECLARATIONS_DIGEST, declarationFromManifest, settingsDeclarations, settingsGroup, valueProblem, type ManifestSetting, type PolicyManifest, type SettingDeclaration, type SettingsMode, type SettingsPolicy } from '@aioe/shared';
import { Segment } from '@heroui-pro/react';
import { useMemo, useState } from 'react';

import { routes } from '../api/client.ts';
import { useDraft, useLoad } from '../hooks.ts';
import { ChoiceSelect, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, SaveBar, StatusMark, Toggle } from './ui.tsx';

const GROUP_TEXT: Record<string, string> = {
  node: 'Nodes',
  git: 'Git',
  llm: 'Models',
  ui: 'Appearance',
  remote: 'Remote control',
  memory: 'Memory',
  research: 'Research',
  facilitator: 'Facilitator',
  localRunner: 'Local runner',
  runtimes: 'Runtimes',
  tasks: 'Tasks',
  teams: 'Teams',
  notifications: 'Notifications',
  updates: 'Updates',
  terminal: 'Terminal',
  editor: 'Editor',
  platform: 'Organisation',
  gateway: 'Gateway',
  observability: 'Observability',
  docs: 'Documents',
  pipelines: 'Pipelines',
  devcontainer: 'Dev containers',
  browser: 'Browser',
  dictation: 'Dictation',
  mcp: 'MCP',
  agents: 'Agents',
  chat: 'Chat',
  chatApprovals: 'Chat approvals',
  ops: 'Operations',
  usage: 'Usage',
  azure: 'Azure',
  bedrock: 'Bedrock',
  routines: 'Routines',
  review: 'Review',
  github: 'GitHub',
  workspace: 'Workspace',
  targets: 'Targets',
  officeLayout: 'Office',
};
export const groupTitle = (g: string) => GROUP_TEXT[g] ?? g.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
export const rowTitle = (key: string) => {
  const rest = key.includes('.') ? key.slice(key.indexOf('.') + 1) : key;
  return rest.replace(/\./g, ' · ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
};

/** Text a person typed, as the value the declaration expects; undefined when it does not fit. */
function parseValue(d: SettingDeclaration, text: string): { value?: unknown; problem?: string } {
  const t = text.trim();
  switch (d.type) {
    case 'number': {
      const n = Number(t);
      return t === '' || Number.isNaN(n) ? { problem: 'expects a number' } : { value: n };
    }
    case 'array':
      return { value: t ? t.split(/[\n,]/).map((x) => x.trim()).filter(Boolean) : [] };
    case 'object':
      try {
        return { value: JSON.parse(t || '{}') };
      } catch {
        return { problem: 'expects JSON' };
      }
    default:
      return { value: t };
  }
}
const showValue = (d: SettingDeclaration, v: unknown): string => (v === undefined ? '' : d.type === 'array' && Array.isArray(v) ? v.join(', ') : d.type === 'object' ? JSON.stringify(v) : String(v));

/** A manifest's setting as the row editor reads it: the declaration shape, with what the manifest adds. */
export function declarationOf(m: ManifestSetting): SettingDeclaration & { manifest: ManifestSetting } {
  return { ...declarationFromManifest(m), manifest: m };
}

/**
 * The settings editor. With a manifest (the newest build the organisation's machines run) the rows,
 * groups and order are the manifest's, non-lockable settings are left out (the endpoint would ignore
 * them in a policy) and each row says who enforces it and what it touches; without one the console's
 * bundled declarations stand in.
 */
export function SettingsPolicyEditor({ value, inherited, canEdit, onSave, title, hint, manifest, draftKey }: { value: SettingsPolicy; inherited?: SettingsPolicy; canEdit: boolean; onSave: (p: SettingsPolicy) => Promise<void>; title: string; hint: string; manifest?: PolicyManifest; /** Keeps unsaved changes in sessionStorage under this key, so a sign-in renewal loses nothing. */ draftKey?: string }) {
  const [draft, setDraft, discard] = useDraft<SettingsPolicy>(draftKey, value);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string>();
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(value);
  const rows = useMemo<Record<string, SettingDeclaration & { manifest?: ManifestSetting }>>(() => {
    if (!manifest) return settingsDeclarations;
    const out: Record<string, SettingDeclaration & { manifest?: ManifestSetting }> = {};
    for (const m of manifest.settings) if (m.lockable) out[m.key] = declarationOf(m);
    return out;
  }, [manifest]);
  const groupOf = (key: string) => rows[key]?.manifest?.group ?? settingsGroup(key);
  const titleOf = (g: string) => manifest?.ui.groups.find((x) => x.id === g)?.title ?? groupTitle(g);
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const by = new Map<string, string[]>();
    for (const key of Object.keys(rows).sort()) {
      const d = rows[key]!;
      if (q && !key.toLowerCase().includes(q) && !(d.description ?? '').toLowerCase().includes(q)) continue;
      const g = groupOf(key);
      by.set(g, [...(by.get(g) ?? []), key]);
    }
    const order = new Map(manifest?.ui.groups.map((g) => [g.id, g.order]) ?? []);
    return [...by.entries()].sort((a, b) => (order.get(a[0]) ?? 1e9) - (order.get(b[0]) ?? 1e9) || titleOf(a[0]).localeCompare(titleOf(b[0])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, rows, manifest]);
  const notOffered = manifest ? manifest.settings.filter((m) => !m.lockable).length : 0;
  const set = (key: string, v: unknown | undefined, mode?: SettingsMode) => {
    const settings = { ...draft.settings };
    const settingsMode = { ...draft.settingsMode };
    if (v === undefined) {
      delete settings[key];
      delete settingsMode[key];
    } else {
      settings[key] = v;
      if (mode) settingsMode[key] = mode;
    }
    setDraft({ settings, settingsMode });
  };
  const save = async () => {
    setSaving(true);
    setError(undefined);
    try {
      await onSave(draft);
      discard();
    } catch (e) {
      const b = (e as { body?: { problems?: string[]; message?: string } }).body;
      setError(b?.problems?.join('. ') ?? b?.message ?? (e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  const governed = Object.keys(draft.settings).length;
  const changed = new Set([...Object.keys(draft.settings), ...Object.keys(value.settings)].filter((k) => JSON.stringify(draft.settings[k]) !== JSON.stringify(value.settings[k]) || draft.settingsMode[k] !== value.settingsMode[k])).size;
  return (
    <>
    <ListGroup title={title} count={governed || undefined} description={hint}>
      <ListBlock>
        <input className={inputClass} aria-label="Find a setting" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a setting" />
        {manifest ? (
          <p className="m-0 mt-2 text-xs text-muted">
            From AI Workbench {manifest.version}’s own description of itself{notOffered ? `; ${notOffered} ${notOffered === 1 ? 'setting is' : 'settings are'} a person’s own and not offered here` : ''}.
          </p>
        ) : null}
      </ListBlock>
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      {groups.length === 0 ? <ListBlock className="text-sm text-muted">No setting matches.</ListBlock> : null}
      {groups.flatMap(([g, keys]) => {
        const set_ = keys.filter((k) => draft.settings[k] !== undefined).length;
        const shown = open === g || Boolean(search.trim());
        return [
          <ListRow
            key={g}
            title={titleOf(g)}
            trailing={
              <span className="text-xs text-muted tabular-nums">
                {set_ ? `${set_} governed · ` : ''}
                {keys.length} {keys.length === 1 ? 'setting' : 'settings'}
              </span>
            }
            onPress={() => setOpen(shown && !search.trim() ? undefined : g)}
          />,
          shown ? (
            <ListBlock key={`${g}/open`} flush>
              <ul className="m-0 list-none p-0">
                {keys.map((key) => (
                  <SettingRow key={key} settingKey={key} d={rows[key]!} value={draft.settings[key]} mode={draft.settingsMode[key]} inherited={inherited?.settings[key]} inheritedMode={inherited?.settingsMode[key]} canEdit={canEdit} onChange={(v, m) => set(key, v, m)} />
                ))}
              </ul>
            </ListBlock>
          ) : null,
        ];
      })}
    </ListGroup>
    <SaveBar isOpen={canEdit && dirty} label={`${changed} ${changed === 1 ? 'setting' : 'settings'} changed`} onDiscard={discard} onSave={() => void save()} saveLabel="Save" saving={saving} />
    </>
  );
}

const ENFORCER_TEXT = { workbench: 'the desktop app', node: 'the node', both: 'the workbench on a desktop or a node' } as const;

/** What a value a person has not typed yet starts from: the product's default where it has one. */
function startingValue(d: SettingDeclaration): unknown {
  if (d.default !== undefined) return d.default;
  if (d.type === 'boolean') return false;
  if (d.enum?.length) return d.enum[0];
  return undefined;
}

const MODE_TEXT = { unset: 'Not set', default: 'Suggested', locked: 'Locked' } as const;

/**
 * One setting: a three-way answer (Not set, Suggested, Locked), and the value only once it is set.
 * Suggested is where machines start and people may change it; Locked wins and they cannot.
 */
export function SettingRow({ settingKey, d, value, mode, inherited, inheritedMode, canEdit, onChange }: { settingKey: string; d: SettingDeclaration & { manifest?: ManifestSetting }; value: unknown; mode?: SettingsMode; inherited?: unknown; inheritedMode?: SettingsMode; canEdit: boolean; onChange: (v: unknown | undefined, mode?: SettingsMode) => void }) {
  const governed = value !== undefined;
  const [text, setText] = useState(showValue(d, value));
  const [problem, setProblem] = useState<string>();
  /** Chosen Suggested or Locked for a setting with no starting value: the field shows, and nothing is set until it holds one. */
  const [pending, setPending] = useState<SettingsMode>();
  const effectiveMode: SettingsMode | undefined = governed ? (mode ?? 'default') : pending;
  const commit = (raw: string) => {
    setText(raw);
    const parsed = parseValue(d, raw);
    const p = parsed.problem ?? (parsed.value !== undefined ? valueProblem(d, parsed.value) : undefined);
    setProblem(p);
    if (!p && parsed.value !== undefined) {
      onChange(parsed.value, effectiveMode ?? 'default');
      setPending(undefined);
    }
  };
  const choose = (next: 'unset' | SettingsMode) => {
    if (next === 'unset') {
      setPending(undefined);
      setText('');
      setProblem(undefined);
      onChange(undefined);
      return;
    }
    if (governed) return onChange(value, next);
    const start = startingValue(d);
    if (start === undefined) setPending(next);
    else {
      setText(showValue(d, start));
      onChange(start, next);
    }
  };
  const control =
    d.type === 'boolean' ? (
      <Toggle label={rowTitle(settingKey)} checked={value === true} onChange={(on) => onChange(on, effectiveMode ?? 'default')} />
    ) : d.enum ? (
      <ChoiceSelect
        label={rowTitle(settingKey)}
        value={governed ? String(value) : undefined}
        options={Object.fromEntries(d.enum.map((x, i) => [String(x), d.enumDescriptions?.[i] ? `${String(x)}: ${d.enumDescriptions[i]}` : String(x)]))}
        onChange={(k) => onChange(d.enum!.find((x) => String(x) === k), effectiveMode ?? 'default')}
      />
    ) : (
      <input aria-label={rowTitle(settingKey)} className={`${inputClass} sm:max-w-xs`} disabled={!canEdit} value={text} onChange={(e) => commit(e.target.value)} placeholder={showValue(d, d.default) || 'Type a value'} />
    );
  const context = !governed
    ? inherited !== undefined
      ? `Organisation: ${showValue(d, inherited)}${inheritedMode === 'locked' ? ', locked' : ''}`
      : d.default !== undefined && d.default !== ''
        ? `Product default: ${showValue(d, d.default)}`
        : undefined
    : undefined;
  const applied = d.manifest
    ? [`Applied by ${ENFORCER_TEXT[d.manifest.enforcedBy]}`, d.manifest.machineSpecific ? 'names something on one machine, so one value for every machine rarely fits' : '', d.manifest.scopes.includes('repo') ? 'a repository may commit its own' : ''].filter(Boolean).join(' · ')
    : undefined;
  return (
    <li className="grid gap-2 border-t border-separator px-4 py-3 text-sm first:border-t-0">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 basis-64">
          <div className="font-medium" title={`${settingKey}${applied ? `\n${applied}` : ''}`}>
            {rowTitle(settingKey)}
          </div>
          {d.description && <div className="text-xs text-muted">{d.description}</div>}
          {context && <div className="mt-0.5 text-xs text-muted">{context}</div>}
        </div>
        <Segment aria-label={`How the organisation sets ${rowTitle(settingKey)}`} size="sm" isDisabled={!canEdit} selectedKey={effectiveMode ?? 'unset'} onSelectionChange={(k) => choose(String(k) as 'unset' | SettingsMode)} className="w-fit shrink-0">
          {(Object.keys(MODE_TEXT) as Array<keyof typeof MODE_TEXT>).map((k) => (
            <Segment.Item key={k} id={k}>
              {MODE_TEXT[k]}
            </Segment.Item>
          ))}
        </Segment>
      </div>
      {effectiveMode ? <div className="flex flex-wrap items-center justify-end gap-2">{control}</div> : null}
      {problem && <div className="text-xs text-danger">{problem}</div>}
    </li>
  );
}

/**
 * Which machines run declarations the platform holds: the editor's keys are sure to exist on those.
 * A machine whose digest no held manifest carries runs a build the platform has not seen yet (it
 * is asked for its manifest and learns it then); that is news, never drift.
 */
export function DeclarationsCheck() {
  const devices = useLoad(() => routes.devices().catch(() => []), [], 60_000);
  const manifests = useLoad(() => routes.policyManifests().catch(() => []), [], 60_000);
  const rows = devices.data ?? [];
  if (!rows.length) return null;
  const known = new Set([...(manifests.data ?? []).map((m) => m.settingsDigest).filter((d): d is string => !!d), ...((manifests.data ?? []).length ? [] : [SETTINGS_DECLARATIONS_DIGEST])]);
  const held = rows.filter((d) => d.settingsDigest && known.has(d.settingsDigest));
  const newer = rows.filter((d) => d.settingsDigest && !known.has(d.settingsDigest));
  const silent = rows.filter((d) => !d.settingsDigest);
  const text =
    newer.length === 0 && silent.length === 0
      ? `Every machine runs settings declarations the platform holds, so every key here is known there.`
      : `${held.length} of ${rows.length} machines run declarations the platform holds${newer.length ? `; ${newer.length} run a build the platform has not seen yet (${newer.map((d) => d.hostname).slice(0, 4).join(', ')}${newer.length > 4 ? ', …' : ''}), which is being asked for its manifest` : ''}${silent.length ? `; ${silent.length} have not reported theirs` : ''}.`;
  return (
    <p className="text-sm">
      <StatusMark state={newer.length ? 'idle' : silent.length ? 'idle' : 'connected'} label={text} />
    </p>
  );
}
